import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  LayoutChangeEvent,
  Platform,
  FlatList,
  ViewToken,
} from 'react-native';
import { IEvent } from '@/types/admin/events';
import { LoadingSpinner } from '@/components';
import { LazyImage } from '@/components/common/admin/mobile/LazyImage';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useRemoteImageAspect } from '@/hooks/common/useRemoteImageAspect';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard, SectionHeader } from '@/components/shared/redesign';

const GRID_COLLAPSE_WIDTH = 340;

interface EventInfoTabProps {
  event: IEvent;
}

export const EventInfoTab: React.FC<EventInfoTabProps> = ({ event }) => {
  const { redesign } = useAdminColors();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [imageAspect, setImageAspect] = useState<number | null>(null); // height / width

  const onViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      if (viewableItems.length > 0 && viewableItems[0].index !== null) {
        setActiveImageIndex(viewableItems[0].index ?? 0);
      }
    },
  ).current;

  const viewabilityConfig = useRef({ itemVisiblePercentThreshold: 50 }).current;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Measure actual image aspect ratio using shared hook
  const measuredAspect = useRemoteImageAspect(
    event.photos?.[activeImageIndex]?.photoUrl || null,
  );
  useEffect(() => {
    setImageAspect(measuredAspect);
  }, [measuredAspect]);

  const [gridContainerWidth, setGridContainerWidth] = useState(0);

  const onGridContainerLayout = (e: LayoutChangeEvent) => {
    setGridContainerWidth(e.nativeEvent.layout.width);
  };

  const isNarrow = gridContainerWidth > 0 && gridContainerWidth < GRID_COLLAPSE_WIDTH;

  const onImageContainerLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  const minImageHeight = 200;
  const maxImageHeight = Platform.OS === 'web' ? 360 : 300;
  const computedImageHeight =
    imageAspect && containerWidth
      ? Math.min(
          maxImageHeight,
          Math.max(minImageHeight, Math.round(containerWidth * imageAspect)),
        )
      : minImageHeight;
  const displayImageHeight = computedImageHeight;

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Images */}
      {event.photos && event.photos.length > 0 && (
        <View style={styles.imageSection} onLayout={onImageContainerLayout}>
          <View
            style={[
              styles.imageFrame,
              {
                height: displayImageHeight,
                backgroundColor: redesign.card.bg,
              },
            ]}
          >
            {containerWidth > 0 && (
              <FlatList
                data={event.photos}
                keyExtractor={(item, index) => item.id?.toString() ?? index.toString()}
                renderItem={({ item }) => (
                  <LazyImage
                    source={{ uri: item.photoUrl ?? '' }}
                    style={{ width: containerWidth, height: displayImageHeight }}
                    resizeMode={Platform.OS === 'web' ? 'contain' : 'cover'}
                    placeholder={
                      <View
                        style={[
                          styles.imagePlaceholder,
                          {
                            width: containerWidth,
                            height: displayImageHeight,
                            backgroundColor: redesign.glass.buttonBg,
                          },
                        ]}
                      >
                        <LoadingSpinner message="Loading image..." />
                      </View>
                    }
                  />
                )}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                onViewableItemsChanged={onViewableItemsChanged}
                viewabilityConfig={viewabilityConfig}
              />
            )}
            {event.photos.length > 1 && (
              <View style={styles.dotsOverlay} pointerEvents="none">
                <View style={styles.dotsBackground}>
                  {event.photos.map((_, index) => (
                    <View
                      key={index}
                      testID={`event-info-dot-${index}`}
                      style={[styles.dot, index === activeImageIndex && styles.dotActive]}
                    />
                  ))}
                </View>
              </View>
            )}
          </View>
        </View>
      )}

      {/* Description */}
      <View style={styles.section}>
        <Text style={[styles.description, { color: redesign.text.primary }]}>
          {event.description}
        </Text>
      </View>

      {/* Event Details */}
      <GlassCard
        padding={16}
        borderRadius={12}
        style={styles.detailsCard}
        onLayout={onGridContainerLayout}
      >
        <View style={styles.detailsGrid}>
          {[
            {
              Icon: SVG_COMPONENTS.CALENDAR,
              label: 'DATE',
              value: formatDate(event.startTime),
            },
            {
              Icon: SVG_COMPONENTS.CLOCK,
              label: 'TIME',
              value: `${formatTime(event.startTime)} - ${formatTime(event.endTime)}`,
            },
            {
              Icon: SVG_COMPONENTS.LOCATION,
              label: 'LOCATION',
              value: event.location,
            },
            {
              Icon: SVG_COMPONENTS.GROUP,
              label: 'RSVP LIMIT',
              value: event.rsvpLimit === 0 ? 'Unlimited' : String(event.rsvpLimit),
            },
          ].map(({ Icon, label, value }, idx, arr) => (
            <View
              key={label}
              testID="event-info-detail-cell"
              style={[
                styles.detailCell,
                isNarrow ? styles.detailCellFull : styles.detailCellHalf,
                isNarrow && idx === arr.length - 1 && styles.detailCellLast,
              ]}
            >
              <View style={[styles.detailIcon, { backgroundColor: redesign.glass.buttonBg }]}>
                <Icon width={20} height={20} color={redesign.text.muted} />
              </View>
              <View style={styles.detailContent}>
                <Text style={[styles.detailLabel, { color: redesign.text.subtle }]}>{label}</Text>
                <Text
                  style={[styles.detailValue, { color: redesign.text.primary }]}
                  numberOfLines={2}
                >
                  {value}
                </Text>
              </View>
            </View>
          ))}
        </View>
      </GlassCard>

      {/* Internal Notes */}
      {event.internalNotes && (
        <View style={styles.section}>
          <SectionHeader title="Internal Notes" />
          <GlassCard padding={12} borderRadius={8}>
            <Text style={[styles.internalNotes, { color: redesign.text.muted }]}>
              {event.internalNotes}
            </Text>
          </GlassCard>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  imageSection: {
    // marginBottom: 8,
  },
  imageFrame: {
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
  },
  imagePlaceholder: {
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dotsOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 12,
    alignItems: 'center',
  },
  dotsBackground: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingVertical: 6,
    gap: 6,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 3.5,
    backgroundColor: 'rgba(255,255,255,0.35)',
  },
  dotActive: {
    width: 14,
    backgroundColor: '#FFFFFF',
  },
  section: {
    marginBottom: 24,
  },
  description: {
    fontSize: 16,
    lineHeight: 24,
  },
  detailsCard: {
    marginBottom: 24,
  },
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  detailCell: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  detailCellHalf: {
    width: '50%',
    paddingRight: 12,
    marginBottom: 14,
  },
  detailCellFull: {
    width: '100%',
    marginBottom: 14,
  },
  detailCellLast: {
    marginBottom: 0,
  },
  detailIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  detailContent: {
    flex: 1,
    minWidth: 0,
  },
  detailLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  detailValue: {
    fontSize: 16,
  },
  internalNotes: {
    fontSize: 14,
    lineHeight: 20,
  },
});
